import type { IPublicClientApplication } from "@azure/msal-browser";
import { apiDelete, apiFetch, apiPost, resolveBlobIdUrl } from "./client";
import { createImageUploadFormData } from "./uploadRequest";
import type { OrnamentDetail, OrnamentGalleryItem, Paged } from "../types/api";

type ApiVersion = { id: string; blobId: string; photoBlobId: string; createdAt: string; supersededAt: string };

type ApiModel = {
  id: string;
  bowColor: string;
  backgroundRemoved: boolean;
  blobId: string;
  photoBlobId: string;
  previousVersions: ApiVersion[];
};

type ApiOrnament = {
  id: string;
  models: ApiModel[];
  image: { blobId: string };
};

type ApiGalleryItem = {
  id: string;
  createdAt: string;
  photoBlobId: string | null;
  bowColor: string | null;
  imageBlobId: string;
};

export interface OrnamentVariant {
  bowColor: string;
  removeBackground: boolean;
  removalMode: string;
}

export const DEFAULT_ORNAMENT_VARIANT: OrnamentVariant = { bowColor: "red", removeBackground: false, removalMode: "rembg" };

function toOrnament({ id, image, models }: ApiOrnament): OrnamentDetail {
  return {
    id,
    image: { id: image.blobId, url: resolveBlobIdUrl(image.blobId) },
    models: models.map((m) => ({
      id: m.id,
      bowColor: m.bowColor,
      backgroundRemoved: m.backgroundRemoved,
      url: resolveBlobIdUrl(m.blobId),
      photoId: m.photoBlobId,
      photoUrl: resolveBlobIdUrl(m.photoBlobId),
      previousVersions: m.previousVersions.map((v) => ({
        id: v.id,
        url: resolveBlobIdUrl(v.blobId),
        previewUrl: resolveBlobIdUrl(v.photoBlobId),
        createdAt: v.createdAt,
        supersededAt: v.supersededAt,
      })),
    })),
  };
}

export function fetchOrnaments(
  msal: IPublicClientApplication,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Paged<OrnamentGalleryItem>> {
  return apiFetch<Paged<ApiGalleryItem>>(`/web/ornament?page=${page}&pageSize=${pageSize}`, msal, { signal }).then(
    ({ items, ...rest }) => ({
      ...rest,
      items: items.map((item) => ({
        id: item.id,
        createdAt: item.createdAt,
        bowColor: item.bowColor,
        photoUrl: item.photoBlobId === null ? null : resolveBlobIdUrl(item.photoBlobId),
        imageUrl: resolveBlobIdUrl(item.imageBlobId),
      })),
    }),
  );
}

export function fetchOrnament(msal: IPublicClientApplication, id: string, signal?: AbortSignal): Promise<OrnamentDetail> {
  return apiFetch<ApiOrnament>(`/web/ornament/${id}`, msal, { signal }).then(toOrnament);
}

export async function generateOrnament(
  msal: IPublicClientApplication,
  image: File,
  variant: OrnamentVariant,
  forceGenerate: boolean,
): Promise<OrnamentDetail> {
  const formData = await createImageUploadFormData(image, { ...variant, forceGenerate });
  return toOrnament(await apiFetch<ApiOrnament>("/web/ornament/generate", msal, { method: "POST", body: formData }));
}

/** (Re)generate one bow colour / background variant for an existing ornament. */
export function regenerateOrnament(msal: IPublicClientApplication, id: string, variant: OrnamentVariant): Promise<OrnamentDetail> {
  return apiPost<ApiOrnament>(`/web/ornament/${id}/generate`, msal, variant).then(toOrnament);
}

export function deleteOrnament(msal: IPublicClientApplication, id: string): Promise<void> {
  return apiDelete(`/web/ornament/${id}`, msal);
}
